import { makeAutoObservable, runInAction, type AnnotationsMap } from "mobx";

import type { RecordBatchMetaResponse, RecordMetaResponse } from "@/lib/types";
import type {
  FetchRecordMetaUseCase,
  FetchRecordsMetaBatchUseCase,
} from "@/application/records/use-cases";
import { getAuthSessionId } from "@/infrastructure/auth/AuthSessionManager";
import {
  DEFAULT_RECORDS_META_BATCH_SIZE,
  type RecordsMetaBatchRequestOptions,
} from "@/lib/records-meta-batch";
import { getRecordMetaSummary } from "@/lib/record-meta-summary";
import { isTransportError } from "@/infrastructure/http/transport-error";

class RecordMetaEntry {
  data: RecordMetaResponse | null = null;
  isLoading = false;
  error: string | null = null;
  loadedAt: number | null = null;

  constructor(readonly cacheKey: string) {
    makeAutoObservable(this, { cacheKey: false }, { autoBind: true });
  }

  setLoading(value: boolean) {
    this.isLoading = value;
  }

  setError(message: string | null) {
    this.error = message;
    if (message) {
      this.loadedAt = Date.now();
    }
  }

  setData(data: RecordMetaResponse) {
    this.data = data;
    this.loadedAt = Date.now();
  }
}

class RecordsMetaBatchEntry {
  data: Record<string, RecordBatchMetaResponse> | null = null;
  isLoading = false;
  error: string | null = null;
  loadedAt: number | null = null;
  private dataHashes = new Map<string, string>();

  constructor(readonly cacheKey: string) {
    makeAutoObservable(
      this,
      { cacheKey: false },
      { autoBind: true, deep: true } // Enable deep observability for nested properties
    );
  }

  setLoading(value: boolean) {
    this.isLoading = value;
  }

  setError(message: string | null) {
    this.error = message;
    if (message) {
      this.loadedAt = Date.now();
    }
  }

  setData(data: Record<string, RecordBatchMetaResponse>) {
    if (!this.data) {
      this.data = {};
    }

    const target = this.data as Record<string, RecordBatchMetaResponse>;
    const nextHashes = new Map<string, string>();
    let changed = false;

    const nextKeys = Object.keys(data);
    const nextKeySet = new Set(nextKeys);

    for (const key of Object.keys(target)) {
      if (!nextKeySet.has(key)) {
        delete target[key];
        this.dataHashes.delete(key);
        changed = true;
      }
    }

    for (const key of nextKeys) {
      const nextMeta = data[key];
      const serialized = stableSerialize(nextMeta);
      nextHashes.set(key, serialized);

      const previousHash = this.dataHashes.get(key);
      if (previousHash !== serialized) {
        target[key] = nextMeta;
        changed = true;
      }
    }

    this.dataHashes = nextHashes;

    if (changed) {
      this.loadedAt = Date.now();
    }
  }
}

export interface RecordsMetaStoreDependencies {
  fetchRecordMetaUseCase: FetchRecordMetaUseCase;
  fetchRecordsMetaBatchUseCase: FetchRecordsMetaBatchUseCase;
}

export class RecordsMetaStore {
  private readonly fetchRecordMetaUseCase: FetchRecordMetaUseCase;
  private readonly fetchRecordsMetaBatchUseCase: FetchRecordsMetaBatchUseCase;

  private readonly metaEntries = new Map<string, RecordMetaEntry>();
  private readonly batchEntries = new Map<string, RecordsMetaBatchEntry>();
  private readonly batchRecordCache = new Map<string, RecordBatchMetaResponse>();
  private readonly batchLoadPromises = new Map<string, Promise<void>>();

  constructor(deps: RecordsMetaStoreDependencies) {
    this.fetchRecordMetaUseCase = deps.fetchRecordMetaUseCase;
    this.fetchRecordsMetaBatchUseCase = deps.fetchRecordsMetaBatchUseCase;

    makeAutoObservable(
      this,
      {
        fetchRecordMetaUseCase: false,
        fetchRecordsMetaBatchUseCase: false,
        metaEntries: false,
        batchEntries: false,
        batchRecordCache: false,
        batchLoadPromises: false,
      } as AnnotationsMap<RecordsMetaStore, never>,
      { autoBind: true }
    );
  }

  getRecordMetaEntry(recordId: string, include?: string[]): RecordMetaEntry {
    const key = this.toSingleKey(recordId, include);
    let entry = this.metaEntries.get(key);
    if (!entry) {
      entry = new RecordMetaEntry(key);
      this.metaEntries.set(key, entry);
    }
    return entry;
  }

  getRecordsMetaBatchEntry(
    recordIds: string[],
    include?: string[],
    mode: "full" | "summary" = "full"
  ): RecordsMetaBatchEntry {
    const key = this.toBatchKey(recordIds, include, mode);
    let entry = this.batchEntries.get(key);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _isNew = !entry;
    if (!entry) {
      entry = new RecordsMetaBatchEntry(key);
      this.batchEntries.set(key, entry);
    }
    entry.setData(this.createBatchDataFromCache(recordIds, include, mode));
    return entry;
  }

  async loadRecordMeta(
    recordId: string,
    include?: string[],
    options?: { force?: boolean }
  ): Promise<void> {
    const entry = this.getRecordMetaEntry(recordId, include);
    if (entry.isLoading && !options?.force) {
      return;
    }

    entry.setLoading(true);
    entry.setError(null);

    const requestSessionId = getAuthSessionId();

    try {
      const data = await this.fetchRecordMetaUseCase.execute({
        recordId,
        include,
      });

      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        entry.setData(data);
        entry.setLoading(false);
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load record meta";
      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        entry.setError(message);
        entry.setLoading(false);
      });
      throw error;
    }
  }

  async loadRecordsMetaBatch(
    recordIds: string[],
    options?: RecordsMetaBatchRequestOptions & { force?: boolean }
  ): Promise<void> {
    const include = options?.include;
    const mode = options?.mode ?? "full";
    const cacheKey = this.toBatchKey(recordIds, include, mode);
    const entry = this.getRecordsMetaBatchEntry(recordIds, include, mode);
    const existingPromise = this.batchLoadPromises.get(cacheKey);
    if (existingPromise && !options?.force) {
      await existingPromise;
      return;
    }

    const loadPromise = (async () => {
      entry.setLoading(true);
      entry.setError(null);

      const requestSessionId = getAuthSessionId();
      const sortedRecordIds = recordIds.slice().sort();
      const missingRecordIds = sortedRecordIds.filter(
        (recordId) =>
          !this.batchRecordCache.has(
            this.toPerRecordKey(recordId, include, mode)
          )
      );

      try {
        if (missingRecordIds.length > 0) {
          const batchSize = Math.max(
            1,
            options?.batchSize ?? DEFAULT_RECORDS_META_BATCH_SIZE
          );

          for (
            let index = 0;
            index < missingRecordIds.length;
            index += batchSize
          ) {
            if (options?.signal?.aborted) {
              throw new DOMException("Aborted", "AbortError");
            }

            const chunk = missingRecordIds.slice(index, index + batchSize);
            const data = await this.fetchRecordsMetaBatchUseCase.execute({
              recordIds: chunk,
              options: {
                include,
                signal: options?.signal,
                transportConfig: options?.transportConfig,
                mode: options?.mode,
              },
            });

            runInAction(() => {
              if (requestSessionId !== getAuthSessionId()) {
                return;
              }

              Object.entries(data).forEach(([recordId, value]) => {
                this.batchRecordCache.set(
                  this.toPerRecordKey(recordId, include, mode),
                  value
                );
              });

              entry.setData(
                this.createBatchDataFromCache(sortedRecordIds, include, mode)
              );
            });
          }
        }

        runInAction(() => {
          if (requestSessionId !== getAuthSessionId()) {
            return;
          }
          entry.setData(
            this.createBatchDataFromCache(sortedRecordIds, include, mode)
          );
        });
      } catch (error) {
        if (isAbortRequestError(error)) {
          runInAction(() => {
            if (requestSessionId !== getAuthSessionId()) {
              return;
            }
            entry.setData(
              this.createBatchDataFromCache(sortedRecordIds, include, mode)
            );
          });
          return;
        }

        const message =
          error instanceof Error
            ? error.message
            : "Failed to load records metadata";
        runInAction(() => {
          if (requestSessionId !== getAuthSessionId()) {
            return;
          }
          entry.setError(message);
        });
        throw error;
      } finally {
        runInAction(() => {
          if (requestSessionId !== getAuthSessionId()) {
            return;
          }
          entry.setLoading(false);
        });
      }
    })();

    this.batchLoadPromises.set(cacheKey, loadPromise);

    try {
      await loadPromise;
    } finally {
      if (this.batchLoadPromises.get(cacheKey) === loadPromise) {
        this.batchLoadPromises.delete(cacheKey);
      }
    }
  }

  async refreshRecord(recordId: string): Promise<void> {
    const singleEntries = Array.from(this.metaEntries.entries()).filter(
      ([key]) => key.startsWith(`${recordId}|`)
    );

    if (singleEntries.length === 0) {
      await this.loadRecordMeta(recordId, undefined, { force: true });
    } else {
      await Promise.all(
        singleEntries.map(([key]) => {
          const [, includeKey] = key.split("|");
          const include =
            includeKey && includeKey !== "*"
              ? includeKey
                  .split(",")
                  .map((part) => part.trim())
                  .filter(Boolean)
              : undefined;

          return this.loadRecordMeta(recordId, include, { force: true });
        })
      );
    }

    const requestSessionId = getAuthSessionId();

    try {
      const freshMeta = await this.fetchRecordMetaUseCase.execute({
        recordId,
        include: undefined,
      });

      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        this.batchRecordCache.forEach((_value, key) => {
          if (key.startsWith(`${recordId}|`)) {
            this.batchRecordCache.delete(key);
          }
        });

        this.batchRecordCache.set(
          this.toPerRecordKey(recordId, undefined, "full"),
          freshMeta
        );

        const summary = getRecordMetaSummary(freshMeta);
        if (summary) {
          this.batchRecordCache.set(this.toPerRecordKey(recordId, undefined, "summary"), {
            ...freshMeta,
            available_versions: undefined,
            report_summary: summary,
          });
        }

        this.batchEntries.forEach((entry) => {
          if (entry.data && entry.data[recordId]) {
            const [idsKey, includeKey, modeKey] = entry.cacheKey.split("|");
            const include =
              includeKey && includeKey !== "*"
                ? includeKey
                    .split(",")
                    .map((part) => part.trim())
                    .filter(Boolean)
                : undefined;
            const mode =
              modeKey === "summary" ? "summary" : "full";
            entry.data = this.createBatchDataFromCache(
              idsKey.split(",").filter(Boolean),
              include,
              mode
            );
            entry.loadedAt = Date.now();
          }
        });
      });
    } catch (error) {
      console.error(
        `[RecordsMetaStore] Failed to update batch entries for record ${recordId}:`,
        error
      );
      // Don't throw - single entries are already updated
    }
  }

  clear(): void {
    this.metaEntries.clear();
    this.batchEntries.clear();
    this.batchRecordCache.clear();
  }

  removeFromCache(recordIds: string[]): void {
    if (!recordIds.length) {
      return;
    }

    const idSet = new Set(recordIds);

    this.metaEntries.forEach((entry, key) => {
      const [recordId] = key.split("|");
      if (idSet.has(recordId)) {
        this.metaEntries.delete(key);
      }
    });

    this.batchEntries.forEach((entry, key) => {
      const ids = key.split("|")[0].split(",");
      if (ids.some((id) => idSet.has(id))) {
        this.batchEntries.delete(key);
      }
    });

    this.batchRecordCache.forEach((_value, key) => {
      const [recordId] = key.split("|");
      if (idSet.has(recordId)) {
        this.batchRecordCache.delete(key);
      }
    });
  }

  private toSingleKey(recordId: string, include?: string[]): string {
    const includeKey = include?.length ? include.slice().sort().join(",") : "*";
    return `${recordId}|${includeKey}`;
  }

  private toBatchKey(
    recordIds: string[],
    include?: string[],
    mode: "full" | "summary" = "full"
  ): string {
    const idsKey = recordIds.slice().sort().join(",");
    const includeKey = include?.length ? include.slice().sort().join(",") : "*";
    return `${idsKey}|${includeKey}|${mode}`;
  }

  private toPerRecordKey(
    recordId: string,
    include?: string[],
    mode: "full" | "summary" = "full"
  ): string {
    const includeKey = include?.length ? include.slice().sort().join(",") : "*";
    return `${recordId}|${includeKey}|${mode}`;
  }

  private createBatchDataFromCache(
    recordIds: string[],
    include?: string[],
    mode: "full" | "summary" = "full"
  ): Record<string, RecordBatchMetaResponse> {
    const data: Record<string, RecordBatchMetaResponse> = {};

    recordIds.forEach((recordId) => {
      const cached = this.batchRecordCache.get(
        this.toPerRecordKey(recordId, include, mode)
      );
      if (cached) {
        data[recordId] = cached;
      }
    });

    return data;
  }
}

function isAbortRequestError(error: unknown): boolean {
  return (
    (error instanceof Error && error.name === "AbortError") ||
    (isTransportError(error) && error.code === "REQUEST_ABORTED")
  );
}

function stableSerialize(value: unknown): string {
  return JSON.stringify(sortValue(value));
}

function sortValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortValue);
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).map(
      ([key, nested]) => [key, sortValue(nested)] as const
    );

    entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return Object.fromEntries(entries);
  }

  return value;
}
